<template>
  <div class="settings-container">
    <div class="settings-card">
      <h2 class="settings-title">
        <i class="pi pi-cog mr-2"></i>Настройки
      </h2>
      <div class="settings-section">
        <h3 class="section-title">
          <i class="pi pi-sync mr-2"></i>Cинхронизации
        </h3>
        <div class="formgrid grid mt-3">
          <div class="field col-12 md:col-3">
            <label for="update_monitoring" class="field-label">
              <i class="pi pi-eye mr-2"></i>Период опроса мониторинга, сек
            </label>
            <InputNumber id="update_monitoring" v-model="setting.monitoringUpdate" mode="decimal" showButtons :min="14"
              :max="120" :step="2" inputClass="w-full" @update:modelValue="changeMonitoringUpdate" />
            <small class="field-hint">Интервал от 14 до 120 секунд</small>
          </div>
          <div class="field col-12 md:col-3">
            <label for="update_property" class="field-label">
              <i class="pi pi-bell mr-2"></i>Период опроса событий/свойств, сек
            </label>
            <InputNumber id="update_property" v-model="setting.propertyUpdate" mode="decimal" showButtons :min="10"
              :max="100" :step="2" inputClass="w-full" @update:modelValue="changePropertyUpdate" />
            <small class="field-hint">Интервал от 10 до 100 секунд</small>
          </div>
          <div class="field col-12 md:col-3">
            <label for="update_property" class="field-label">
              <i class="pi pi-bell mr-2"></i>Период обновления событий, сек
            </label>
            <InputNumber id="update_property" v-model="setting.eventUpdate" mode="decimal" showButtons :min="120"
              :max="6000" :step="2" inputClass="w-full" @update:modelValue="changeEventUpdate" />
            <small class="field-hint">Интервал от 120 до 6000 секунд</small>
          </div>
        </div>
      </div>
      <div class="settings-section mt-1">
        <div class="flex align-items-center mt-1">
          <Checkbox v-model="setting.hideRepeatWell" binary :inputId="'hideRepeatWell'" @change="changeHideRepeatWell"
            class="mr-3" />
          <label class="checkbox-label">
            Скрывать повторы скважин
            <small class="checkbox-hint block mt-1">
              Включает фильтрацию дублирующихся скважин для удобного просмотра
            </small>
          </label>

          <label title="Виды сортировки" class="checkbox-label">
            Разбивка сортировки объектов
            <SelectButton v-model="setting.structMode" :options="modeOptions" optionLabel="label" optionValue="value"
              @update:modelValue="changeStructMode" />

          </label>
        </div>
        <div class="flex align-items-center mt-3">
          <Checkbox v-model="setting.enableEventSound" binary :inputId="'enableEventSound'"
            @change="changeEnableEventSound" class="mr-3" />
          <label class="checkbox-label">
            Звуковое оповещение
            <small class="checkbox-hint block mt-1">
              Включает звуковое оповещение событий
            </small>
          </label>
          <Checkbox v-model="setting.enableEventFacilitySound" binary :inputId="'enableEventFacilitySound'"
            @change="changeEnableEventFacilitySound" class="mr-3" />
          <label class="checkbox-label">
            Оповещение по объектам
            <small class="checkbox-hint block mt-1">
              Включает наименование объекта в оповещение
            </small>
          </label>
          <div>
            <Slider v-model="setting.speechRate" :inputId="'speechRate'" @change="changeSpeechRate" :min="0.1" :max="10"
              :step="0.1" :disabled="!setting.enableEventSound" />
            <label class="checkbox-label">
              Скорость речи {{ setting.speechRate }}
            </label>
          </div>
          <div class="ml-4">
            <Slider v-model="setting.speechPitch" :inputId="'speechPitch'" @change="changeSpeechPitch" :min="0" :max="2"
              :step="0.1" :disabled="!setting.enableEventSound" />
            <label class="checkbox-label">
              Высота тона {{ setting.speechPitch }}
            </label>
          </div>
        </div>
      </div>

      <div class="settings-section mt-1">
        <h3 class="section-title">
          <i class="pi pi-link mr-2"></i>Подключение
        </h3>
        <div class="connection-grid">
          <div class="connection-item">
            <div class="connection-label">
              <i class="pi pi-user mr-2"></i>Логин пользователя:
            </div>
            <div class="connection-value">
              <Tag :value="login" severity="info" />
            </div>
          </div>
          <div class="connection-item">
            <div class="connection-label">
              <i class="pi pi-server mr-2"></i>Конфигурация сервера:
            </div>
            <div class="connection-value">
              <Tag :value="VUE_APP_SERVER_NAME" severity="success" />
            </div>
          </div>
          <div class="connection-item">
            <div class="connection-label">
              <i class="pi pi-compass mr-2"></i>Профиль подключения:
            </div>
            <div class="connection-value">
              <Tag :value="direction" severity="warning" />
            </div>
          </div>
          <div class="connection-item">
            <div class="connection-label">
              <i class="pi pi-database mr-2"></i>Сервер REST API:
            </div>
            <div class="connection-value">
              <span class="text-overflow">
                <a target="_blank" :href="serverUrl">{{ serverUrl }}</a>
              </span>
            </div>
          </div>
          <div class="connection-item">
            <div class="connection-label">
              <i class="pi pi-file mr-2"></i>Адаптер отчетов:
            </div>
            <div class="connection-value">
              <span class="text-overflow">
                <a target="_blank" :href="adapterUrl">{{ adapterUrl }}</a>
              </span>
            </div>
          </div>
          <div class="connection-item">
            <div class="connection-label">
              <i class="pi pi-folder-open mr-2"></i>Подключение для отчетов:
            </div>
            <div class="connection-value">
              <span class="text-overflow" :title="connectionPath">{{ connectionPath }}</span>
            </div>
          </div>
          <div class="connection-item">
            <div class="connection-label">
              <i class="pi pi-directions-alt mr-2"></i>Средний слой:
            </div>
            <div class="connection-value">
              <span class="text-overflow">
                <a target="_blank" :href="middleUrl">{{ middleUrl }}</a>
              </span>
            </div>
          </div>
          <div class="connection-item">
            <div class="connection-label">
              <i class="pi pi-key mr-2"></i>До смены пароля, д.:
            </div>
            <div class="connection-value">
              <Tag :value="passwordExpirationDay" :severity="tagExpirationDay" />
            </div>
          </div>
        </div>
      </div>
      <div class="settings-footer mt-5">
        <Button icon="pi pi-sitemap" label="Диагностика" severity="warning" @click="showDiagnostic = true" />
      </div>
    </div>
  </div>

  <Diagnostic v-model:showDiagnostic="showDiagnostic" />
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { useStore } from 'vuex';
import Diagnostic from './Diagnostic.vue';

const store = useStore();

const VUE_APP_SERVER_NAME = ref(process.env.VUE_APP_SERVER_NAME || '');

const setting = reactive({
  monitoringUpdate: store.getters.monitoringUpdate,
  propertyUpdate: store.getters.propertyUpdate,
  eventUpdate: store.getters.eventUpdate,
  hideRepeatWell: store.getters.hideRepeatWell,
  structMode: store.getters.structMode,
  enableEventSound: store.getters.enableEventSound,
  enableEventFacilitySound: store.getters.enableEventFacilitySound,
  speechRate: store.getters.speechRate,
  speechPitch: store.getters.speechPitch,
});

const showDiagnostic = ref(false);
const modeOptions = [
  { label: 'По позиции', value: 1, hint: 'По кустам' },
  { label: 'По типу(тест)', value: 2, hint: 'По скважинам' },
  { label: 'По алфавиту', value: 3, hint: '' },
];
const direction = computed(() => store.getters.direction);
const serverUrl = computed(() => store.getters.server('SERVER'));
const adapterUrl = computed(() => store.getters.server('ADAPTER'));
const middleUrl = computed(() => store.getters.server('TM_MIDDLE'));
const connectionPath = computed(() => store.getters.connectionPath);
const info = computed(() => store.getters.info);
const login = computed(() => info.value?.login || '');

const passwordExpirationDay = computed(() => {
  if (!info.value?.passwordExpirationDate) return null;
  const now = new Date();
  const exp = new Date(info.value.passwordExpirationDate);
  const diff = Math.abs(now - exp);
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
});

const tagExpirationDay = computed(() => {
  const days = passwordExpirationDay.value;
  if (days === null) return 'info';
  if (days >= 20 && days < 50) return 'warning';
  if (days < 20) return 'danger';
  return 'info';
})

const changeEventUpdate = (value) => {
  store.dispatch('setEventUpdate', value);
}
const changePropertyUpdate = (value) => {
  store.dispatch('setPropertyUpdate', value);
}
const changeMonitoringUpdate = (value) => {
  store.dispatch('setMonitoringUpdate', value);
}
const changeHideRepeatWell = () => {
  store.dispatch('setHideRepeatWell', setting.hideRepeatWell);
}
const changeStructMode = (e) => {
  store.dispatch('setStructMode', e);
}
const changeEnableEventSound = () => {
  store.dispatch('setEnableEventSound', setting.enableEventSound);
}
const changeEnableEventFacilitySound = () => {
  store.dispatch('setEnableEventFacilitySound', setting.enableEventFacilitySound);
}
const changeSpeechRate = () => {
  console.log(setting.speechRate);

  store.dispatch('setSpeechRate', setting.speechRate);
}
const changeSpeechPitch = () => {
  store.dispatch('setSpeechPitch', setting.speechPitch);
}

</script>

<style lang="scss" scoped>
.settings-container {
  padding: 1.2rem;
}

.settings-card {
  background: var(--surface-card);
  border-radius: var(--border-radius-xl);
  padding: 2rem;
  box-shadow: var(--card-shadow);
  border: 1px solid var(--surface-border);
  max-width: 1600px;
  margin: 0 auto;
}

.settings-title {
  color: var(--surface-900);
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--surface-border);
  display: flex;
  align-items: center;
}

.settings-section {
  padding: 1rem;
  background: var(--surface-ground);
  border-radius: var(--border-radius);
  border: 1px solid var(--surface-border);


  &:last-child {
    margin-bottom: 0;
  }
}

.section-title {
  color: var(--surface-700);
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

.field {
  margin-bottom: 1.5rem;
}

.field-label {
  display: flex;
  align-items: center;
  color: var(--surface-700);
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.field-hint {
  color: var(--surface-500);
  font-size: 0.75rem;
  margin-top: 0.25rem;
  display: block;
}

.checkbox-label {
  color: var(--surface-700);
  font-weight: 500;
  user-select: none;
  flex: 1;
}

.checkbox-hint {
  color: var(--surface-500);
  font-size: 0.8rem;
  font-weight: normal;
}

.connection-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 0.5fr));
  gap: 0.5rem;
  margin-top: 1rem;
}

.connection-item {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: var(--surface-ground);
  border-radius: var(--border-radius);
  border: 1px solid var(--surface-border);
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface-hover);
    transform: translateX(2px);
  }
}

.connection-label {
  flex: 0 0 auto;
  color: var(--surface-600);
  font-size: 0.875rem;
  min-width: 180px;
  display: flex;
  align-items: center;
  font-weight: 500;
}

.connection-value {
  flex: 1;
  margin-left: 1rem;
  display: block;
  min-width: 0;
}

.text-overflow {
  white-space: nowrap;
  overflow: auto;
  max-width: 100%;
  color: var(--surface-900);
  font-weight: 500;
  display: block;
}

.settings-footer {
  border-top: 1px solid var(--surface-border);
  padding-top: 1.5rem;
  text-align: center;
}

@media (max-width: 768px) {
  .settings-container {
    padding: 1rem;
  }

  .settings-card {
    padding: 1.5rem;
  }

  .connection-grid {
    grid-template-columns: 1fr;
  }

  .connection-item {
    width: 70%;
    flex-direction: column;
    align-items: flex-start;
  }

  .connection-label {
    min-width: auto;
    margin-bottom: 0.5rem;
  }

  .connection-value {
    margin-left: 0;
    width: 100%;
    min-width: 0;
  }
}

@media (max-width: 480px) {
  .settings-card {
    padding: 1rem;
  }

  .settings-title {
    font-size: 1.25rem;
  }

  .section-title {
    font-size: 1rem;
  }
}
</style>